import {
  AutoAwesome as AutoAwesomeIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  DeleteSweep as DeleteSweepIcon,
  Edit as EditIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  MoveToInbox as MoveIcon,
  Notifications as NotificationsIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Schedule as ScheduleIcon,
  TouchApp as TouchAppIcon,
  Update as UpdateIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

import { Automation } from '../types';

interface AutomationCardProps {
  automation: Automation;
  onToggleActive: (id: string, isActive: boolean) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

export default function AutomationCard({
  automation,
  onToggleActive,
  onEdit,
  onDelete,
}: AutomationCardProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleToggleActive = () => {
    onToggleActive(automation.id, !automation.isActive);
    handleMenuClose();
  };

  const handleEdit = () => {
    onEdit(automation.id);
    handleMenuClose();
  };

  const handleDelete = () => {
    onDelete(automation.id);
    handleMenuClose();
  };

  const getTriggerIcon = (trigger: string) => {
    switch (trigger) {
      case 'entity':
        return <BusinessIcon />;
      case 'time':
        return <ScheduleIcon />;
      case 'manual':
        return <TouchAppIcon />;
      default:
        return <AutoAwesomeIcon />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon color="success" />;
      case 'paused':
        return <PauseIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <WarningIcon color="warning" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Paper elevation={1} sx={{ mb: 2 }}>
      <CardContent>
        <Stack spacing={2}>
          {/* Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              {getTriggerIcon(automation.trigger)}
              <Stack>
                <Typography variant="h6" fontWeight={500}>
                  {automation.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {automation.description}
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              {getStatusIcon(automation.status)}
              <Chip
                label={automation.isActive ? 'Active' : 'Inactive'}
                color={getStatusColor(automation.status) as any}
                size="small"
              />
              <IconButton size="small" onClick={handleMenuClick}>
                <MoreVertIcon />
              </IconButton>
            </Stack>
          </Stack>

          <Divider />

          {/* Details */}
          <Accordion variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Automation Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                    <Stack>
                      <Typography variant="subtitle2" color="text.secondary">
                        Trigger
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {getTriggerIcon(automation.trigger)}
                        <Typography variant="body2">
                          {automation.trigger.charAt(0).toUpperCase() +
                            automation.trigger.slice(1)}{' '}
                          Based
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body2"
                        sx={{ mt: 1, fontStyle: 'italic' }}
                      >
                        {automation.triggerLogic}
                      </Typography>
                    </Stack>

                    <Stack>
                      <Typography variant="subtitle2" color="text.secondary">
                        Condition
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <FilterIcon fontSize="small" />
                        <Typography variant="body2">
                          {automation.condition}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                    <Stack>
                      <Typography variant="subtitle2" color="text.secondary">
                        Actions
                      </Typography>
                      <List dense>
                        {automation.actions.map((action, index) => (
                          <ListItem
                            key={index}
                            sx={{
                              py: 0,
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{ width: '100%' }}
                            >
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                {action.includes('notification') && (
                                  <NotificationsIcon fontSize="small" />
                                )}
                                {action.includes('Update') && (
                                  <UpdateIcon fontSize="small" />
                                )}
                                {action.includes('Move') && (
                                  <MoveIcon fontSize="small" />
                                )}
                                {action.includes('Delete') && (
                                  <DeleteSweepIcon fontSize="small" />
                                )}
                                {action.includes('report') && (
                                  <AutoAwesomeIcon fontSize="small" />
                                )}
                                {action.includes('email') && (
                                  <NotificationsIcon fontSize="small" />
                                )}
                                {action.includes('meeting') && (
                                  <AutoAwesomeIcon fontSize="small" />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={action}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  fontWeight: 500,
                                }}
                              />
                            </Stack>
                            {automation.actionDetails[index] && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ ml: 4, mt: 0.5, fontStyle: 'italic' }}
                              >
                                {automation.actionDetails[index]}
                              </Typography>
                            )}
                          </ListItem>
                        ))}
                      </List>
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>

              {/* Timing Info */}
              {(automation.lastRun || automation.nextRun) && (
                <Stack direction="row" spacing={4} mt={2}>
                  {automation.lastRun && (
                    <Stack>
                      <Typography variant="subtitle2" color="text.secondary">
                        Last Run
                      </Typography>
                      <Typography variant="body2">
                        {automation.lastRun}
                      </Typography>
                    </Stack>
                  )}
                  {automation.nextRun && (
                    <Stack>
                      <Typography variant="subtitle2" color="text.secondary">
                        Next Run
                      </Typography>
                      <Typography variant="body2">
                        {automation.nextRun}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        </Stack>
      </CardContent>

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleToggleActive}>
          {automation.isActive ? (
            <>
              <PauseIcon sx={{ mr: 1 }} />
              Disable
            </>
          ) : (
            <>
              <PlayIcon sx={{ mr: 1 }} />
              Enable
            </>
          )}
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Paper>
  );
}
